"use client";
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Link from "next/link";

const cards = [
  {
    id: "pre",
    title: "Pre-Retirement",
    rotate: -6,
    desc: "Start your pre-retirement investment plan now.",
    bg: "/next_assets/images/financial-planning/pre-retirement.svg",
  },
  {
    id: "post",
    title: "Post-Retirement",
    rotate: 6,
    desc: "Grow your post-retirement savings smartly.",
    bg: "/next_assets/images/financial-planning/post-retirement.svg",
  },
] as const;

export default function RetirementHeroSection() {
  const [match, setMatch] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 630px)");
    const update = () => setMatch(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const isActive = (id: string) => (hovered ? hovered === id : id === "pre");

  return (
    <section className="pb-17.5 relative">
      <div className="flex gap-3 xxs:justify-center overflow-auto scrollbar-none xxs:overflow-visible px-4 mb-6">
        {cards.map(({ id, title, desc, rotate, bg }) => (
          <motion.div
            key={id}
            onMouseEnter={() => setHovered(id)}
            animate={
              match
                ? {
                    scale: isActive(id) ? 1.05 : 1,
                    rotate,
                  }
                : { scale: 1, rotate: 0 }
            }
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 20,
            }}
            style={{
              backgroundImage: `url(${bg})`,
              zIndex: isActive(id) ? 3 : 1,
              mixBlendMode: match ? (isActive(id) ? "normal" : "luminosity") : "normal",
              border: isActive(id) && match ? "3px solid #000" : "none",
            }}
            className="relative w-72.25 h-400 rounded-6xl bg-cover bg-center cursor-pointer shrink-0"
          >
            <Link href={`/financial-planning/retirement-planning/${id.toLowerCase()}-retirement`} aria-label="Retirement Planning Process" className="block">
              <div className="absolute inset-0 flex flex-col justify-end px-5 py-8 text-center text-white">
                <p className="text-xl font-bold mb-2">{title}</p>
                <p className="text-sm mb-4">{desc}</p>
                <motion.div
                  initial={false}
                  animate={match ? (isActive(id) ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }) : { opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  className="bg-white text-black font-semibold text-sm rounded-full py-3 px-12 mx-auto"
                  style={{
                    pointerEvents: isActive(id) ? "auto" : "none",
                  }}
                >
                  Start Planning
                </motion.div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
